import React from 'react';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { get } from 'lodash';
import { CompanyListDto } from 'models/dtoTypes';
import { CompanyListAttributes } from '../../models';
import ResourceTable, { IColumn } from '../../components/ResourceTable';
import { GetCompanyLists, CreateCompanyList } from 'services/companyList';
import CreateResourceModal, { ISchema } from '../../dialogs/CreateResourceModal';
import Notice from '../../components/Notice';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import generateNonce from 'utils/generateNonce';
import Link from 'components/Link';
import CurrentUser from 'auth/currentUser';

const PREFIX = 'CompanyListsPage';

const classes = {
  root: `${PREFIX}-root`,
  pageHeader: `${PREFIX}-pageHeader`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(5),
  width: '100%',

  [`& .${classes.pageHeader}`]: {
    width: '100%',
    position: 'relative',

    '& > button': {
      position: 'absolute',
      right: 0,
      top: 0,
    }
  },

}));

const getColumns = (isAdmin?: boolean) => {
  const columns: IColumn<CompanyListDto>[] = [
    {
      label: 'Name',
      sortIndex: 'name',
      render: (model: CompanyListDto) => {
        return (
          <Link to={`/companyLists/${model?.id || ''}`} text={model?.name || ''} />
        );
      }
    },
    {
      label: 'Description',
      dataIndex: 'description',
    },
    {
      label: 'Description',
      dataIndex: 'description',
    },
    ...(isAdmin
      ? [{
        label: 'Organisation',
        render: (model: CompanyListDto) => {
          const orgName = model?.organisation?.name as string;
          const orgId = model?.organisationId as string;
          if (orgName) {
            return <Link to={`/organisations/${orgId || ''}`} text={orgName} />;
          } else {
            return <></>;
          }
        },
      }]
      : []
    ),
    {
      label: 'Company Count',
      dataIndex: 'companyCount',
    },
  ];
  return columns;
};


const getSchema = (isAdmin?: boolean) => {
  const schema: ISchema = {
    fields: [
      {
        label: 'List Name',
        name: 'name',
        required: true,
        widget: 'text',
        description: ''
      },
      {
        label: 'Description',
        name: 'description',
        required: true,
        widget: 'text',
      }
    ]
  };

  if (isAdmin) {
    schema.fields.push({
      label: 'Organisation',
      name: 'organisationId',
      required: true,
      widget: 'organisationSelector',
    });
  }
  return schema;
};

interface INoticeData {
  message: string;
  severity: 'error' | 'success';
}

export default function CompanyListsPage({ isAdmin }: { isAdmin?: boolean }): JSX.Element {
  const [openCreate, setOpenCreate] = React.useState(false);
  const [nonce, setNonce] = React.useState('');
  const [noticeData, setNoticeData] = React.useState<INoticeData>();
  const organisationId = CurrentUser.getInstance().getOrganisationId();

  const handleError = (e: unknown) => {
    if (axios.isAxiosError(e) && e.response) {
      console.log(e.response?.data);
      const errorMessage = get(e.response?.data, 'message') || 'Something went wrong';
      setNoticeData({
        message: errorMessage,
        severity: 'error',
      });
    } else {
      // native error
      console.error(e);
      setNoticeData({
        message: e instanceof Error ? e.message : String(e),
        severity: 'error',
      });
    }
  };

  const handleCloseNotice = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setNoticeData(undefined);
  };

  const handleCreate = async (attrs: CompanyListAttributes) => {
    try {
      await CreateCompanyList(attrs);
      setNonce(generateNonce);
      setOpenCreate(false);
      setNoticeData({
        message: 'Successfully created.',
        severity: 'success',
      });
    } catch (e) {
      handleError(e);
    }
  };

  const filterSchema = [
    {
      label: 'Name Contains',
      name: 'name',
      widget: 'text',
    },
  ];

  return (
    <Root>
      <div className={classes.pageHeader}>
        <h2>Company Lists</h2>
        <Button onClick={() => setOpenCreate(true)} variant="contained" startIcon={<AddIcon />}>
          Create
        </Button>
      </div>
      <ResourceTable
        filterSchema={filterSchema}
        key={nonce}
        columns={getColumns(isAdmin)}
        onFetch={GetCompanyLists}
        defaultQuery={!isAdmin
          ? [{
            column: 'organisationId',
            value: organisationId,
          }]
          : undefined}
        defaultSort={{
          sortColumn: 'created',
          isDesc: true
        }}
      />
      <CreateResourceModal<CompanyListAttributes>
        open={openCreate}
        label="Create a new company list"
        schema={getSchema(isAdmin)}
        onClose={() => setOpenCreate(false)}
        onSave={handleCreate}
      />
      {noticeData && (
        <Notice
          open={true}
          message={noticeData.message}
          severity={noticeData.severity}
          onClose={handleCloseNotice}
        />
      )}
    </Root>
  );
}
